/**
 * SwitchExamples.tsx
 * Comprehensive examples of HeroUI Switch component
 * 
 * Showcases all HeroUI Switch features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Switch } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  Bell,
  Wifi,
  Shield,
  Eye,
  Volume2,
  Bluetooth,
  Smartphone,
  Moon,
  Sun
} from 'lucide-react';

export const SwitchExamples: React.FC = () => {
  // Individual switch states
  const [switchStates, setSwitchStates] = useState<Record<string, boolean>>({
    basic: false,
    selected: true,
    primary: false,
    secondary: false,
    success: true,
    warning: false,
    danger: false,
    small: false,
    medium: true,
    large: false,
    disabled: false,
    notifications: true,
    darkMode: false,
    wifi: true,
    bluetooth: false
  });

  const updateSwitchState = (key: string) => (isSelected: boolean) => {
    setSwitchStates(prev => ({ ...prev, [key]: isSelected }));
  };

  const getSwitchState = (key: string): boolean => {
    return switchStates[key] ?? false;
  };

  const sections = [
    {
      title: "Basic Usage",
      description: "Simple switches with different states",
      examples: [
        {
          title: "Default Switch",
          component: (
            <Switch 
              isSelected={getSwitchState('basic')}
              onValueChange={updateSwitchState('basic')}
            >
              Enable feature
            </Switch>
          ),
          code: `<Switch isSelected={isSelected} onValueChange={setSelected}>
  Enable feature
</Switch>`
        },
        {
          title: "Initially Selected",
          component: (
            <Switch 
              isSelected={getSwitchState('selected')}
              onValueChange={updateSwitchState('selected')}
            >
              Auto-save enabled
            </Switch>
          ),
          code: `<Switch isSelected={true} onValueChange={setSelected}>
  Auto-save enabled
</Switch>`
        },
        {
          title: "Disabled Switch",
          component: (
            <Switch isDisabled>
              Premium feature (requires upgrade)
            </Switch>
          ),
          code: `<Switch isDisabled>
  Premium feature (requires upgrade)
</Switch>`
        }
      ]
    },
    {
      title: "Colors",
      description: "All available color variants with theme integration",
      examples: [
        {
          title: "Color Variants",
          component: (
            <div className="space-y-4">
              <Switch 
                color="primary" 
                isSelected={getSwitchState('primary')}
                onValueChange={updateSwitchState('primary')}
              >
                Primary
              </Switch>
              <Switch 
                color="secondary" 
                isSelected={getSwitchState('secondary')}
                onValueChange={updateSwitchState('secondary')}
              >
                Secondary
              </Switch>
              <Switch 
                color="success" 
                isSelected={getSwitchState('success')}
                onValueChange={updateSwitchState('success')}
              >
                Success
              </Switch>
              <Switch 
                color="warning" 
                isSelected={getSwitchState('warning')}
                onValueChange={updateSwitchState('warning')}
              >
                Warning
              </Switch>
              <Switch 
                color="danger" 
                isSelected={getSwitchState('danger')}
                onValueChange={updateSwitchState('danger')}
              >
                Danger
              </Switch>
            </div>
          ),
          code: `<Switch color="primary" isSelected={isSelected}>Primary</Switch>
<Switch color="secondary" isSelected={isSelected}>Secondary</Switch>
<Switch color="success" isSelected={isSelected}>Success</Switch>
<Switch color="warning" isSelected={isSelected}>Warning</Switch>
<Switch color="danger" isSelected={isSelected}>Danger</Switch>`
        }
      ]
    },
    {
      title: "Sizes",
      description: "Different size variants",
      examples: [
        {
          title: "Size Variants",
          component: (
            <div className="space-y-4">
              <Switch 
                size="sm" 
                color="primary"
                isSelected={getSwitchState('small')}
                onValueChange={updateSwitchState('small')}
              >
                Small
              </Switch>
              <Switch 
                size="md" 
                color="primary"
                isSelected={getSwitchState('medium')}
                onValueChange={updateSwitchState('medium')}
              >
                Medium (default)
              </Switch>
              <Switch 
                size="lg" 
                color="primary"
                isSelected={getSwitchState('large')}
                onValueChange={updateSwitchState('large')}
              >
                Large
              </Switch>
            </div>
          ),
          code: `<Switch size="sm" color="primary">Small</Switch>
<Switch size="md" color="primary">Medium</Switch>
<Switch size="lg" color="primary">Large</Switch>`
        }
      ]
    },
    {
      title: "With Icons",
      description: "Switches with start and end content icons",
      examples: [
        {
          title: "Start Content",
          component: (
            <div className="space-y-4">
              <Switch 
                color="primary"
                isSelected={getSwitchState('notifications')}
                onValueChange={updateSwitchState('notifications')}
                startContent={<Bell className="w-4 h-4" />}
              >
                Notifications
              </Switch>
              <Switch 
                color="success"
                isSelected={getSwitchState('wifi')}
                onValueChange={updateSwitchState('wifi')}
                startContent={<Wifi className="w-4 h-4" />}
              >
                WiFi
              </Switch>
              <Switch 
                color="secondary"
                isSelected={getSwitchState('bluetooth')}
                onValueChange={updateSwitchState('bluetooth')}
                startContent={<Bluetooth className="w-4 h-4" />}
              >
                Bluetooth
              </Switch>
            </div>
          ),
          code: `<Switch 
  startContent={<Bell className="w-4 h-4" />}
  color="primary"
>
  Notifications
</Switch>`
        },
        {
          title: "End Content",
          component: (
            <div className="space-y-4">
              <Switch 
                color="warning"
                isSelected={getSwitchState('darkMode')}
                onValueChange={updateSwitchState('darkMode')}
                endContent={getSwitchState('darkMode') ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              >
                {getSwitchState('darkMode') ? 'Dark Mode' : 'Light Mode'}
              </Switch>
              <Switch 
                color="primary"
                defaultSelected
                endContent={<Eye className="w-4 h-4" />}
              >
                Show password
              </Switch>
              <Switch 
                color="success"
                defaultSelected
                endContent={<Volume2 className="w-4 h-4" />}
              >
                Sound effects
              </Switch>
            </div>
          ),
          code: `<Switch 
  endContent={<Moon className="w-4 h-4" />}
  color="warning"
>
  Dark Mode
</Switch>`
        },
        {
          title: "Thumb Icon",
          component: (
            <div className="space-y-4">
              <Switch 
                color="primary"
                defaultSelected
                thumbIcon={<Shield className="w-3 h-3" />}
              >
                Security enabled
              </Switch>
              <Switch 
                color="secondary"
                defaultSelected={false}
                thumbIcon={<Smartphone className="w-3 h-3" />}
              >
                Mobile sync
              </Switch>
            </div>
          ),
          code: `<Switch 
  thumbIcon={<Shield className="w-3 h-3" />}
  color="primary"
>
  Security enabled
</Switch>`
        }
      ]
    },
    {
      title: "Settings Panel Example",
      description: "Real-world usage in a settings interface",
      examples: [
        {
          title: "App Settings",
          component: (
            <Card className="max-w-md">
              <CardHeader>
                <h3 className="text-lg font-semibold">App Settings</h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Push Notifications</div>
                    <div className="text-sm text-text-secondary">Receive alerts and updates</div>
                  </div>
                  <Switch 
                    color="primary"
                    isSelected={getSwitchState('notifications')}
                    onValueChange={updateSwitchState('notifications')}
                    startContent={<Bell className="w-4 h-4" />}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Dark Mode</div>
                    <div className="text-sm text-text-secondary">Switch to dark theme</div>
                  </div>
                  <Switch 
                    color="secondary"
                    isSelected={getSwitchState('darkMode')}
                    onValueChange={updateSwitchState('darkMode')}
                    endContent={getSwitchState('darkMode') ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Auto-Connect WiFi</div>
                    <div className="text-sm text-text-secondary">Connect to known networks</div>
                  </div>
                  <Switch 
                    color="success"
                    isSelected={getSwitchState('wifi')}
                    onValueChange={updateSwitchState('wifi')}
                    startContent={<Wifi className="w-4 h-4" />}
                  />
                </div>
                
                <div className="flex items-center justify-between opacity-50">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      Premium Analytics
                      <Badge color="warning" size="sm">Pro</Badge>
                    </div>
                    <div className="text-sm text-text-secondary">Advanced usage insights</div>
                  </div>
                  <Switch 
                    color="warning"
                    isDisabled
                  />
                </div>
              </CardBody>
            </Card>
          ),
          code: `<div className="flex items-center justify-between">
  <div>
    <div className="font-medium">Push Notifications</div>
    <div className="text-sm text-text-secondary">Receive alerts and updates</div>
  </div>
  <Switch 
    color="primary"
    isSelected={isSelected}
    onValueChange={setSelected}
    startContent={<Bell className="w-4 h-4" />}
  />
</div>`
        }
      ]
    },
    {
      title: "Controlled vs Uncontrolled",
      description: "Different ways to manage switch state",
      examples: [
        {
          title: "Controlled Switch",
          component: (
            <div className="space-y-4">
              <Switch 
                color="primary"
                isSelected={getSwitchState('notifications')}
                onValueChange={updateSwitchState('notifications')}
              >
                Controlled: {getSwitchState('notifications') ? 'On' : 'Off'}
              </Switch>
              <p className="text-sm text-text-secondary">
                State managed by parent component - Click to toggle
              </p>
            </div>
          ),
          code: `const [isSelected, setSelected] = useState(false);

<Switch 
  isSelected={isSelected}
  onValueChange={setSelected}
>
  Controlled Switch
</Switch>`
        },
        {
          title: "Uncontrolled Switch",
          component: (
            <div className="space-y-4">
              <Switch 
                color="secondary"
                defaultSelected={false}
              >
                Uncontrolled Switch
              </Switch>
              <p className="text-sm text-text-secondary">
                State managed internally by component
              </p>
            </div>
          ),
          code: `<Switch defaultSelected={false}>
  Uncontrolled Switch
</Switch>`
        }
      ]
    }
  ];

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Switch Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Switch component with automatic theme integration
          </p>
        </div>

        {sections.map((section, sectionIndex) => (
          <Card key={sectionIndex}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{section.title}</h2>
                <Badge color="primary" variant="flat" size="sm">Toggle control</Badge>
              </div>
            </CardHeader>
            <CardBody className="space-y-6">
              <p className="text-text-secondary">{section.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.examples.map((example, exampleIndex) => (
                  <div key={exampleIndex} className="space-y-3">
                    <h3 className="font-medium text-text-primary">{example.title}</h3>
                    <div className="p-4 bg-content2 rounded-lg min-h-[120px] flex items-center justify-center">
                      {example.component}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}
        
      </div>
    </PlaygroundPageLayout>
  );
};